import { moneyFormat } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { getShopCurrencyWithDefault } from "@redotech/redo-billing-common/team-currency";
import {
  CartValueKind,
  SegmentNumberOperator,
} from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import {
  rcoCartValueSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { t, useTranslation } from "@redotech/redo-i18n";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  RedoCurrencyInput,
  RedoCurrencyInputValue,
} from "@redotech/redo-web/arbiter-components/input/redo-currency-input";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";

export const CartValueInspectorContent = memo(
  function CartValueInspectorContent({ step }: { step: RcoSegmentStep }) {
    const { t } = useTranslation();
    const team = useRequiredContext(TeamContext);
    const shopCurrency = getShopCurrencyWithDefault({ team });

    const segment = useMemo(
      () => rcoCartValueSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Condition")}
        </Text>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {segment.valueKind === CartValueKind.DISCOUNTED
            ? t("Discounted cart value is")
            : t("Total cart value is")}{" "}
          {segmentNumberOperatorToText()[segment.operator]}{" "}
          {moneyFormat({
            amount: segment.value.amount,
            currency: shopCurrency,
          })}
        </Text>
      </Flex>
    );
  },
);

export const EditableCartValueInspectorContent = memo(
  function EditableCartValueInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const { t } = useTranslation();
    const team = useRequiredContext(TeamContext);
    const shopCurrency = getShopCurrencyWithDefault({ team });

    const segment = useMemo(
      () => rcoCartValueSegmentSchema.parse(step.segment),
      [step],
    );

    const segmentValue = useMemo(() => {
      return { amount: segment.value.amount, currency: shopCurrency };
    }, [segment.value, shopCurrency]);

    const handleOperatorChange = useHandler(
      (operator: SegmentNumberOperator) => {
        const updatedSegment = { ...segment, operator };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleValueChange = useHandler((value: RedoCurrencyInputValue) => {
      const updatedSegment = {
        ...segment,
        value: { amount: value.amount ?? 0 },
      };
      setStep({ ...step, segment: updatedSegment });
    });

    const handleValueKindChange = useHandler((valueKind: CartValueKind) => {
      const updatedSegment = { ...segment, valueKind };
      setStep({ ...step, segment: updatedSegment });
    });

    const selectedOperator = useMemo(() => {
      return operatorOptions().find(
        (option) => option.value === segment.operator,
      );
    }, [segment.operator]);

    const selectedValueKind = useMemo(() => {
      return valueKindOptions().find(
        (option) => option.value === segment.valueKind,
      );
    }, [segment.valueKind]);

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
        </Flex>
        <Flex dir="column" gap="md" w="full">
          <Flex align="center" dir="row" gap="md" wrap="wrap">
            <RedoSingleSelectDropdownInput<CartValueKind>
              fitToAnchor={false}
              flexGrow={false}
              options={valueKindOptions()}
              optionSelected={({ item }) => handleValueKindChange(item.value)}
              placeholder={t("Select value type")}
              selectedItem={selectedValueKind}
              size="xs"
            />
            <Text fontSize="xs" fontWeight="medium">
              {t("cart value is")}
            </Text>
            <RedoSingleSelectDropdownInput<SegmentNumberOperator>
              fitToAnchor={false}
              flexGrow={false}
              options={operatorOptions()}
              optionSelected={({ item }) => handleOperatorChange(item.value)}
              placeholder={t("Select comparison")}
              selectedItem={selectedOperator}
              size="xs"
            />
          </Flex>
          <Flex align="center" dir="row" gap="sm">
            <RedoCurrencyInput
              placeholder={0}
              setValue={handleValueChange}
              size="sm"
              value={segmentValue}
            />
          </Flex>
        </Flex>
      </>
    );
  },
);

function operatorOptions(): TextRedoListItem<SegmentNumberOperator>[] {
  return [
    {
      type: "text",
      id: SegmentNumberOperator.LESS_THAN,
      value: SegmentNumberOperator.LESS_THAN,
      text: t("less than"),
    },
    {
      type: "text",
      id: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
      value: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
      text: t("less than or equal to"),
    },
    {
      type: "text",
      id: SegmentNumberOperator.EQUAL,
      value: SegmentNumberOperator.EQUAL,
      text: t("equal to"),
    },
    {
      type: "text",
      id: SegmentNumberOperator.GREATER_THAN,
      value: SegmentNumberOperator.GREATER_THAN,
      text: t("greater than"),
    },
    {
      type: "text",
      id: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
      value: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
      text: t("greater than or equal to"),
    },
  ];
}

function valueKindOptions(): TextRedoListItem<CartValueKind>[] {
  return [
    {
      type: "text",
      id: CartValueKind.TOTAL,
      value: CartValueKind.TOTAL,
      text: t("Total"),
    },
    {
      type: "text",
      id: CartValueKind.DISCOUNTED,
      value: CartValueKind.DISCOUNTED,
      text: t("Discounted"),
    },
  ];
}

function segmentNumberOperatorToText(): Record<SegmentNumberOperator, string> {
  return {
    [SegmentNumberOperator.LESS_THAN]: t("less than"),
    [SegmentNumberOperator.LESS_THAN_OR_EQUAL]: t("less than or equal to"),
    [SegmentNumberOperator.EQUAL]: t("equal to"),
    [SegmentNumberOperator.GREATER_THAN]: t("greater than"),
    [SegmentNumberOperator.GREATER_THAN_OR_EQUAL]: t(
      "greater than or equal to",
    ),
  };
}
